import React, { useState, useEffect } from 'react';
import {
  FiInfo,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiFileText,
  FiX,
  FiCheck,
  FiEdit,
  FiSave,
  FiRefreshCw
} from 'react-icons/fi';
import { FaDownload, FaRupeeSign } from 'react-icons/fa';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { useAuth } from '../../../provider/auth.provider';
import validator from 'validator';
import { formatDate } from '../../activities/edit';
import { LuActivity } from 'react-icons/lu';
import EntityLogs from '../../../utils/logs.utils';

interface GeneralClaim {
  id: number;
  staff_id: number;
  staff_type: string;
  association_type: string;
  claim_title: string;
  claim_description: string;
  claim_date: string;
  claim_category?: string;
  travel_amount?: number;
  da_amount?: number;
  stay_amount?: number;
  food_amount?: number;
  other_amount1?: number;
  other_description1?: string;
  other_amount2?: number;
  other_description2?: string;
  submitted_total?: number;
  approved_travel_amount?: number;
  approved_da_amount?: number;
  approved_stay_amount?: number;
  approved_food_amount?: number;
  approved_other_amount1?: number;
  approved_other_description1?: string;
  approved_other_amount2?: number;
  approved_other_description2?: string;
  final_approved_total?: number;
  status: string;
  submitted_at?: string;
  approved_at?: string;
  approved_by?: number;
  rejection_reason?: string;
  rejected_at?: string;
  bill_attachments?: string[];
  created_at: string;
  updated_at: string;
  approver?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
  staff?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
  rejector?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
}

interface ApprovalFormData {
  approved_travel_amount?: number;
  approved_da_amount?: number;
  approved_stay_amount?: number;
  approved_food_amount?: number;
  approved_other_amount1?: number;
  approved_other_description1?: string;
  approved_other_amount2?: number;
  approved_other_description2?: string;
  kms_slab_category?: string;
}

const GeneralClaimView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [claim, setClaim] = useState<GeneralClaim | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [showApprovalDrawer, setShowApprovalDrawer] = useState<boolean>(false);
  const [showRejectDrawer, setShowRejectDrawer] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [approvalData, setApprovalData] = useState<ApprovalFormData>({});

  // Edit drawer
  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({
    claim_title: '', claim_description: '',
    travel_amount: '', da_amount: '', stay_amount: '', food_amount: '',
    other_amount1: '', other_description1: '', other_amount2: '', other_description2: '',
  });
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({});

  const tabs = [
    { id: 'details', label: 'Claim Details', icon: FiInfo },
    { id: 'amounts', label: 'Amounts', icon: FaRupeeSign },
    { id: 'attachments', label: 'Attachments', icon: FiFileText },
    { id: 'logs', label: 'Logs', icon: LuActivity },
  ];

  useEffect(() => {
    fetchClaimData();
  }, [id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !actionLoading) {
        if (showApprovalDrawer) setShowApprovalDrawer(false);
        if (showRejectDrawer) setShowRejectDrawer(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showApprovalDrawer, showRejectDrawer, actionLoading]);

  const fetchClaimData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/claims/general/${id}`);
      const data = response.data.data;
      setClaim(data);

      if (data) {
        setApprovalData({
          approved_travel_amount: data.travel_amount,
          approved_da_amount: data.da_amount,
          approved_stay_amount: data.stay_amount,
          approved_food_amount: data.food_amount,
          approved_other_amount1: data.other_amount1,
          approved_other_description1: data.other_description1,
          approved_other_amount2: data.other_amount2,
          approved_other_description2: data.other_description2
        });
        setEditForm({
          claim_title: data.claim_title || '',
          claim_description: data.claim_description || '',
          travel_amount: data.travel_amount != null ? String(data.travel_amount) : '',
          da_amount: data.da_amount != null ? String(data.da_amount) : '',
          stay_amount: data.stay_amount != null ? String(data.stay_amount) : '',
          food_amount: data.food_amount != null ? String(data.food_amount) : '',
          other_amount1: data.other_amount1 != null ? String(data.other_amount1) : '',
          other_description1: data.other_description1 || '',
          other_amount2: data.other_amount2 != null ? String(data.other_amount2) : '',
          other_description2: data.other_description2 || '',
        });
      }
    } catch (error) {
      console.error('Error fetching claim:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!claim) return;
    try {
      setEditLoading(true);
      await axiosInstance.put(`/claims/general/${id}/update`, {
        claim_title: editForm.claim_title,
        claim_description: editForm.claim_description,
        travel_amount: editForm.travel_amount !== '' ? Number(editForm.travel_amount) : 0,
        da_amount: editForm.da_amount !== '' ? Number(editForm.da_amount) : 0,
        stay_amount: editForm.stay_amount !== '' ? Number(editForm.stay_amount) : 0,
        food_amount: editForm.food_amount !== '' ? Number(editForm.food_amount) : 0,
        other_amount1: editForm.other_amount1 !== '' ? Number(editForm.other_amount1) : 0,
        other_description1: editForm.other_description1,
        other_amount2: editForm.other_amount2 !== '' ? Number(editForm.other_amount2) : 0,
        other_description2: editForm.other_description2,
      });
      await fetchClaimData();
      setShowEditDrawer(false);
      navigate(`/claims/general/${id}`, { replace: true });
    } catch (error) {
      console.error('Error updating claim:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const validateAmount = (value: number | undefined, maxValue: number | undefined, fieldName: string): string => {
    if (value === undefined || value === null) {
      return '';
    }

    const valueStr = value.toString();

    if (!validator.isNumeric(valueStr, { no_symbols: false })) {
      return `${fieldName} must be a valid number`;
    }

    if (!validator.isFloat(valueStr, { min: 0 })) {
      return `${fieldName} must be zero or greater`;
    }

    if (maxValue !== undefined && value > maxValue) {
      return `${fieldName} cannot exceed submitted amount`;
    }

    return '';
  };

  const validateRejectionReason = (value: string): string => {
    if (!value || validator.isEmpty(value.trim())) {
      return 'Rejection reason is required';
    }

    if (!validator.isLength(value.trim(), { min: 10, max: 500 })) {
      return 'Rejection reason must be between 10 and 500 characters';
    }

    if (!validator.matches(value, /^[a-zA-Z0-9\s.,!?'-]+$/)) {
      return 'Rejection reason contains invalid characters';
    }

    return '';
  };

  const validateApprovalForm = (): boolean => {
    const newErrors: any = {};

    // Validate travel amount
    const travelError = validateAmount(
      approvalData.approved_travel_amount,
      claim?.travel_amount,
      'Travel amount'
    );
    if (travelError) newErrors.approved_travel_amount = travelError;

    // Validate DA amount
    const daError = validateAmount(
      approvalData.approved_da_amount,
      claim?.da_amount,
      'DA amount'
    );
    if (daError) newErrors.approved_da_amount = daError;

    // Validate stay amount
    const stayError = validateAmount(
      approvalData.approved_stay_amount,
      claim?.stay_amount,
      'Stay amount'
    );
    if (stayError) newErrors.approved_stay_amount = stayError;

    // Validate food amount
    const foodError = validateAmount(
      approvalData.approved_food_amount,
      claim?.food_amount,
      'Food amount'
    );
    if (foodError) newErrors.approved_food_amount = foodError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRejectForm = (): boolean => {
    const newErrors: any = {};

    const reasonError = validateRejectionReason(rejectionReason);
    if (reasonError) {
      newErrors.rejectionReason = reasonError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateApprovalForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/general/approve-reject', {
        id: Number(id),
        status: 'approved',
        ...approvalData,
        kms_slab_category: approvalData.kms_slab_category
      });
      await fetchClaimData();
      setShowApprovalDrawer(false);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error approving claim:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!validateRejectForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/general/approve-reject', {
        id: Number(id),
        status: 'rejected',
        rejection_reason: rejectionReason.trim()
      });
      await fetchClaimData();
      setShowRejectDrawer(false);
      setRejectionReason('');
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error rejecting claim:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { icon: FiClock, bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: 'Pending' },
      'submitted': { icon: FiClock, bgColor: 'bg-blue-100', textColor: 'text-blue-800', label: 'Submitted' },
      'approved': { icon: FiCheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Approved' },
      'rejected': { icon: FiAlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const calculateApprovedTotal = () => {
    return (
      (approvalData.approved_travel_amount || 0) +
      (approvalData.approved_da_amount || 0) +
      (approvalData.approved_stay_amount || 0) +
      (approvalData.approved_food_amount || 0) +
      (approvalData.approved_other_amount1 || 0) +
      (approvalData.approved_other_amount2 || 0)
    );
  };

  const renderDetailsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Claim Title</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">{claim?.claim_title}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Category</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.claim_category || 'N/A'}</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-500">Description</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.claim_description}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Claim Date</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.claim_date ? formatDate(claim.claim_date) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Staff Type</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim?.staff_type === 'field' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                {claim?.staff_type}
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Association Type</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${claim?.association_type === 'inhouse' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                }`}>
                {claim?.association_type === 'inhouse' ? 'Onrole' : 'Offrole'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Status Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Current Status</label>
            <p className="text-sm text-gray-900 mt-1">{getStatusBadge(claim?.status || 'pending')}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Submitted At</label>
            <p className="text-sm text-gray-900 mt-1">{claim?.submitted_at ? formatDate(claim.submitted_at) : 'Not submitted'}</p>
          </div>
          {claim?.status === 'approved' && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved At</label>
              <p className="text-sm text-gray-900 mt-1">{claim?.approved_at ? formatDate(claim.approved_at) : 'Not approved'}</p>
            </div>
          )}
          {claim?.approved_by && claim?.status === 'approved' && claim?.approver && (
            <div>
              <label className="text-xs font-medium text-gray-500">Approved By</label>
              <p className="text-sm text-gray-900 mt-1 font-semibold">{`${claim.approver.first_name} ${claim.approver.last_name}`}</p>
              {claim.approver.association_type && (
                <p className="text-xs text-gray-600 mt-1 capitalize">{claim.approver.association_type}</p>
              )}
            </div>
          )}
          {claim?.status === 'rejected' && (
            <>
              <div>
                <label className="text-xs font-medium text-gray-500">Rejected At</label>
                <p className="text-sm text-gray-900 mt-1">{claim?.rejected_at ? formatDate(claim.rejected_at) : 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Rejected By</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">
                  {claim?.rejector ? `${claim.rejector.first_name} ${claim.rejector.last_name}` : 'N/A'}
                </p>
                {claim?.rejector?.association_type && (
                  <p className="text-xs text-gray-600 mt-1 capitalize">{claim.rejector.association_type}</p>
                )}
              </div>
            </>
          )}
          {claim?.rejection_reason && claim?.status === 'rejected' && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">Rejection Reason</label>
              <p className="text-sm text-red-900 mt-1 bg-red-50 p-3 rounded">{claim.rejection_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAmountsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Submitted Amounts</h3>
        <div className="space-y-3">
          {claim?.travel_amount && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Travel Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.travel_amount)}</span>
            </div>
          )}
          {claim?.da_amount && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">DA Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.da_amount)}</span>
            </div>
          )}
          {claim?.stay_amount && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Stay Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.stay_amount)}</span>
            </div>
          )}
          {claim?.food_amount && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Food Amount</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.food_amount)}</span>
            </div>
          )}
          {claim?.other_amount1 && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{claim.other_description1 || 'Other Amount 1'}</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.other_amount1)}</span>
            </div>
          )}
          {claim?.other_amount2 && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">{claim.other_description2 || 'Other Amount 2'}</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.other_amount2)}</span>
            </div>
          )}
          <div className="flex justify-between py-3 border-t-2 border-gray-200 mt-2">
            <span className="text-base font-semibold text-gray-900">Total Submitted</span>
            <span className="text-base font-bold text-blue-900">{formatCurrency(claim?.submitted_total)}</span>
          </div>
        </div>
      </div>

      {claim?.status === 'approved' && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Approved Amounts</h3>
          <div className="space-y-3">
            {claim?.approved_travel_amount && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Travel Amount</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_travel_amount)}</span>
              </div>
            )}
            {claim?.approved_da_amount && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">DA Amount</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_da_amount)}</span>
              </div>
            )}
            {claim?.approved_stay_amount && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Stay Amount</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_stay_amount)}</span>
              </div>
            )}
            {claim?.approved_food_amount && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Food Amount</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_food_amount)}</span>
              </div>
            )}
            {claim?.approved_other_amount1 && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Other Amount 1</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_other_amount1)}</span>
              </div>
            )}
            {claim?.approved_other_amount2 && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Other Amount 2</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(claim.approved_other_amount2)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 border-t-2 border-gray-200 mt-2">
              <span className="text-base font-semibold text-gray-900">Total Approved</span>
              <span className="text-base font-bold text-green-900">{formatCurrency(claim?.final_approved_total)}</span>
            </div>
          </div>
        </div>
      )}

      {claim?.status === 'rejected' && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Approval Summary</h3>
          <p className="text-sm text-gray-600">This claim was rejected. Approval amounts are not available.</p>
        </div>
      )}
    </div>
  );

  const renderAttachmentsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Bill Attachments</h3>
        {claim?.bill_attachments && claim.bill_attachments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {claim.bill_attachments.map((attachment, index) => (
              <div key={index} className="border border-gray-200 rounded p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <FiFileText className="w-8 h-8 text-blue-600" />
                  <a
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaDownload className="w-4 h-4" />
                  </a>
                </div>
                <p className="text-xs text-gray-600 truncate">Attachment {index + 1}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No attachments found</p>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Claim Not Found</h2>
          <p className="text-gray-600 mb-4">The claim you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/claims/general')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Claims
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">General Claim #{claim.id}</h1>
              <p className="text-sm text-gray-600 mt-1">{claim.claim_title}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Claim Date: {formatDate(claim.claim_date)}
                </div>
                <div className="flex items-center gap-1">
                  <FiUser size={12} />
                  Staff: {claim.staff?.first_name} {claim.staff?.last_name}
                </div>
                {claim.status === 'rejected' && claim.rejected_at && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiCalendar size={12} />
                    Rejected: {formatDate(claim.rejected_at)}
                  </div>
                )}
                {claim.status === 'rejected' && claim.rejector && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiUser size={12} />
                    {claim.rejector.first_name} {claim.rejector.last_name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(claim.status)}
              {claim.status === 'submitted' && (
                <>
                  <button
                    onClick={() => setShowApprovalDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiX className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'amounts' && renderAmountsTab()}
        {activeTab === 'attachments' && renderAttachmentsTab()}
        {activeTab === 'logs' && <EntityLogs entity="general_claim" entityId={id as string} />}

        {/* Approval Drawer */}
        {showApprovalDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowApprovalDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Approve Claim</h3>
                  <p className="text-sm text-gray-600 mt-1">Review and adjust claim amounts</p>
                </div>
                <button
                  onClick={() => setShowApprovalDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Travel Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_travel_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_travel_amount: value });
                        setTouched({ ...touched, approved_travel_amount: true });
                        if (errors.approved_travel_amount) {
                          setErrors({ ...errors, approved_travel_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_travel_amount: true });
                        const error = validateAmount(
                          approvalData.approved_travel_amount,
                          claim?.travel_amount,
                          'Travel amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_travel_amount: error });
                        }
                      }}
                      max={claim?.travel_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_travel_amount && touched.approved_travel_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.travel_amount)}</p>
                    {errors.approved_travel_amount && touched.approved_travel_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_travel_amount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DA Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_da_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_da_amount: value });
                        setTouched({ ...touched, approved_da_amount: true });
                        if (errors.approved_da_amount) {
                          setErrors({ ...errors, approved_da_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_da_amount: true });
                        const error = validateAmount(
                          approvalData.approved_da_amount,
                          claim?.da_amount,
                          'DA amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_da_amount: error });
                        }
                      }}
                      max={claim?.da_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_da_amount && touched.approved_da_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.da_amount)}</p>
                    {errors.approved_da_amount && touched.approved_da_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_da_amount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stay Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_stay_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_stay_amount: value });
                        setTouched({ ...touched, approved_stay_amount: true });
                        if (errors.approved_stay_amount) {
                          setErrors({ ...errors, approved_stay_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_stay_amount: true });
                        const error = validateAmount(
                          approvalData.approved_stay_amount,
                          claim?.stay_amount,
                          'Stay amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_stay_amount: error });
                        }
                      }}
                      max={claim?.stay_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_stay_amount && touched.approved_stay_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.stay_amount)}</p>
                    {errors.approved_stay_amount && touched.approved_stay_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_stay_amount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Food Amount
                    </label>
                    <input
                      type="number"
                      value={approvalData.approved_food_amount || ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovalData({ ...approvalData, approved_food_amount: value });
                        setTouched({ ...touched, approved_food_amount: true });
                        if (errors.approved_food_amount) {
                          setErrors({ ...errors, approved_food_amount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approved_food_amount: true });
                        const error = validateAmount(
                          approvalData.approved_food_amount,
                          claim?.food_amount,
                          'Food amount'
                        );
                        if (error) {
                          setErrors({ ...errors, approved_food_amount: error });
                        }
                      }}
                      max={claim?.food_amount || 0}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.approved_food_amount && touched.approved_food_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Submitted: {formatCurrency(claim?.food_amount)}</p>
                    {errors.approved_food_amount && touched.approved_food_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approved_food_amount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      KMS Slab Category
                    </label>
                    <select
                      value={approvalData.kms_slab_category || ''}
                      onChange={(e) => {
                        setApprovalData({ ...approvalData, kms_slab_category: e.target.value });
                        setTouched({ ...touched, kms_slab_category: true });
                      }}
                      disabled={actionLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select KMS Slab Category</option>
                      <option value="0-100">0-100 KMS</option>
                      <option value="100-200">100-200 KMS</option>
                      <option value="200-300">200-300 KMS</option>
                      <option value="300-400">300-400 KMS</option>
                      <option value="400-500">400-500 KMS</option>
                      <option value="500+">500+ KMS</option>
                    </select>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Approved Amount:</span>
                      <span className="text-lg font-bold text-green-900">{formatCurrency(calculateApprovedTotal())}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Approval Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Amounts can only be equal to or less than submitted</li>
                          <li>All amounts must be zero or greater</li>
                          <li>Verify supporting documents before approval</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowApprovalDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Approving...' : 'Approve Claim'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reject Drawer */}
        {showRejectDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowRejectDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Claim</h3>
                  <p className="text-sm text-gray-600 mt-1">Provide a reason for rejection</p>
                </div>
                <button
                  onClick={() => setShowRejectDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => {
                        setRejectionReason(e.target.value);
                        setTouched({ ...touched, rejectionReason: true });
                        if (errors.rejectionReason) {
                          setErrors({ ...errors, rejectionReason: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, rejectionReason: true });
                        const error = validateRejectionReason(rejectionReason);
                        if (error) {
                          setErrors({ ...errors, rejectionReason: error });
                        }
                      }}
                      rows={4}
                      disabled={actionLoading}
                      placeholder="Please provide a detailed reason for rejection (minimum 10 characters)..."
                      maxLength={500}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-red-500 ${errors.rejectionReason && touched.rejectionReason
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {rejectionReason.length}/500 characters (minimum 10)
                    </p>
                    {errors.rejectionReason && touched.rejectionReason && (
                      <p className="mt-1 text-sm text-red-600">{errors.rejectionReason}</p>
                    )}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium mb-1">Rejection Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Provide a clear and specific reason</li>
                          <li>Be professional and constructive</li>
                          <li>Use only alphanumeric characters and basic punctuation</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowRejectDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Rejecting...' : 'Reject Claim'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showEditDrawer && claim && claim.status === 'submitted' && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowEditDrawer(false)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FiEdit className="text-orange-600" size={18} />Edit General Claim
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Only editable while status is "Submitted"</p>
              </div>
              <button onClick={() => setShowEditDrawer(false)} className="p-1.5 hover:bg-gray-200 rounded-lg"><FiX size={18} className="text-gray-500" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={editForm.claim_title} onChange={e => setEditForm(f => ({ ...f, claim_title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                <textarea value={editForm.claim_description} onChange={e => setEditForm(f => ({ ...f, claim_description: e.target.value }))}
                  rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 resize-y" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'travel_amount', label: 'Travel Amount (₹)' },
                  { key: 'da_amount', label: 'DA Amount (₹)' },
                  { key: 'stay_amount', label: 'Stay Amount (₹)' },
                  { key: 'food_amount', label: 'Food Amount (₹)' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                    <input type="number" min="0" value={(editForm as any)[key]}
                      onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Amount 1 (₹)</label>
                  <input type="number" min="0" value={editForm.other_amount1}
                    onChange={e => setEditForm(f => ({ ...f, other_amount1: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Description 1</label>
                  <input type="text" value={editForm.other_description1}
                    onChange={e => setEditForm(f => ({ ...f, other_description1: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Amount 2 (₹)</label>
                  <input type="number" min="0" value={editForm.other_amount2}
                    onChange={e => setEditForm(f => ({ ...f, other_amount2: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Other Description 2</label>
                  <input type="text" value={editForm.other_description2}
                    onChange={e => setEditForm(f => ({ ...f, other_description2: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              {/* Total preview */}
              <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm font-semibold text-gray-900 flex justify-between">
                <span>New Submitted Total</span>
                <span>₹{(
                  (Number(editForm.travel_amount) || 0) + (Number(editForm.da_amount) || 0) +
                  (Number(editForm.stay_amount) || 0) + (Number(editForm.food_amount) || 0) +
                  (Number(editForm.other_amount1) || 0) + (Number(editForm.other_amount2) || 0)
                ).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowEditDrawer(false)} disabled={editLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-orange-700 rounded-lg hover:bg-orange-800 disabled:opacity-50 flex items-center gap-2">
                {editLoading ? <><FiRefreshCw size={14} className="animate-spin" />Saving...</> : <><FiSave size={14} />Save Changes</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralClaimView;